import { useEffect, useState } from "react";
import { motion } from "motion/react";
import ImageWithFallback from "../ui/ImageWithFallback";

function useIsDesktop(breakpointPx = 820) {
    const [isDesktop, setIsDesktop] = useState(() =>
        typeof window !== "undefined"
            ? window.matchMedia(`(min-width:${breakpointPx}px)`).matches
            : true
    );

    useEffect(() => {
        const mq = window.matchMedia(`(min-width:${breakpointPx}px)`);
        const handler = (e) => setIsDesktop(e.matches);

        mq.addEventListener?.("change", handler);
        mq.addListener?.(handler);

        setIsDesktop(mq.matches);

        return () => {
            mq.removeEventListener?.("change", handler);
            mq.removeListener?.(handler);
        };
    }, [breakpointPx]);

    return isDesktop;
}

export default function MembershipSection({ primaryColor, styles }) {
    const isDesktop = useIsDesktop(820);

    // Logo liegt in public/ -> per /assets/... ansprechbar
    const logoSrc = "/assets/zuchtverein/logozuchtverein.jpeg";

    return (
        <section style={{ ...styles.section, backgroundColor: "#F5F0EB" }}>
            <motion.h2
                style={{ ...styles.sectionTitle, color: primaryColor }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
            >
                Unsere Mitgliedschaft
            </motion.h2>

            <motion.p
                style={{
                    ...(styles.zuchtvereinText || {}),
                    maxWidth: 900,
                    margin: "0 auto 18px",
                    textAlign: "center",
                    lineHeight: 1.7,
                }}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
            >
                Wir sind Mitglied im <strong>CDK Ebern e.V.</strong> und orientieren uns an den dort
                definierten Richtlinien und Standards für verantwortungsvolle Zucht.
            </motion.p>

            {/* Single Membership Card */}
            <motion.div
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55 }}
                viewport={{ once: true }}
                style={{
                    maxWidth: 980,
                    margin: "0 auto",
                    background: "#fff",
                    borderRadius: 18,
                    border: "1px solid rgba(0,0,0,0.06)",
                    boxShadow: "0 16px 40px rgba(0,0,0,0.06)",
                    overflow: "hidden",
                }}
            >
                {/* Responsive Layout: Desktop 2 Spalten, Mobile stacked */}
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: isDesktop ? "260px 1fr" : "1fr",
                    }}
                >
                    {/* LOGO AREA */}
                    {logoSrc && (
                        <div
                            style={{
                                background: "linear-gradient(180deg, #FAFAFA 0%, #FFFFFF 100%)",
                                borderRight: isDesktop ? "1px solid rgba(0,0,0,0.06)" : "none",
                                borderBottom: !isDesktop ? "1px solid rgba(0,0,0,0.06)" : "none",
                                padding: isDesktop ? "18px" : "16px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: isDesktop ? 220 : 140,
                            }}
                        >
                            <div
                                style={{
                                    width: "100%",
                                    maxWidth: isDesktop ? 220 : 260,
                                    height: isDesktop ? 160 : 110,
                                    borderRadius: 16,
                                    background: "#fff",
                                    border: "1px solid rgba(0,0,0,0.06)",
                                    boxShadow: "0 10px 22px rgba(0,0,0,0.06)",
                                    padding: 10,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <ImageWithFallback
                                    src={logoSrc}
                                    alt="CDK Ebern e.V. Logo"
                                    style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "contain",
                                        objectPosition: "center",
                                        display: "block",
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* CONTENT AREA */}
                    <div style={{ padding: isDesktop ? "18px 18px 18px" : "16px 16px 18px" }}>
                        {/* Header */}
                        <div style={{ marginBottom: 12 }}>
                            <div style={{ fontWeight: 900, fontSize: 18, color: primaryColor }}>
                                CDK Ebern e.V.
                            </div>
                            <div style={{ fontSize: 13, color: "#666", marginTop: 4, lineHeight: 1.4 }}>
                                Club / Verein • verantwortungsvolle Zuchtstandards
                            </div>
                        </div>

                        {/* Body */}
                        <div
                            style={{
                                display: "grid",
                                gap: 10,
                                fontSize: 14,
                                color: "#333",
                                lineHeight: 1.6,
                            }}
                        >
                            <div>
                                <strong>Warum das wichtig ist:</strong> Die Mitgliedschaft verpflichtet uns zu klaren
                                Regeln rund um Gesundheit, Haltung, Dokumentation und verantwortungsvolle Welpenabgabe.
                            </div>

                            <div
                                style={{
                                    borderTop: "1px solid rgba(0,0,0,0.06)",
                                    paddingTop: 12,
                                    display: "grid",
                                    gap: 8,
                                }}
                            >
                                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 10 }}>
                                    <span style={{ fontWeight: 800, color: "#444" }}>Verein</span>
                                    <span>CDK Ebern e.V.</span>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 10 }}>
                                    <span style={{ fontWeight: 800, color: "#444" }}>Schwerpunkt</span>
                                    <span>Richtlinien & Standards für verantwortungsvolle Zucht</span>
                                </div>

                                {/* Optional: Website später */}
                                {/*
                <div style={{ display: "grid", gridTemplateColumns: "140px 1fr", gap: 10 }}>
                  <span style={{ fontWeight: 800, color: "#444" }}>Website</span>
                  <a href="https://..." target="_blank" rel="noreferrer" style={{ color: primaryColor, fontWeight: 800 }}>
                    Zur Vereinsseite
                  </a>
                </div>
                */}
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            <motion.p
                style={{
                    ...(styles.zuchtvereinFooterText || {}),
                    maxWidth: 980,
                    margin: "18px auto 0",
                    textAlign: "center",
                    lineHeight: 1.7,
                    color: "#555",
                }}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
            >
                Diese Mitgliedschaft steht für Transparenz und verantwortungsvolle Zucht – damit du dich auf
                gesunde, wesensfeste Welpen verlassen kannst.
            </motion.p>
        </section>
    );
}
